import { useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Crosshair,
  Flag,
  Loader2,
  MapPin,
  Package,
  Phone,
  Search,
  Share2,
  Snowflake,
  Truck,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  type ShipmentAddress,
  type Task,
  TaskStatus,
  type Trip,
  TripStatus,
  type Waypoint,
} from "@/api.types.v2";
import {
  useOrderSearch,
  type OrderSearchResult,
  type OrderSearchType,
} from "@/api/orders";
import { useActiveTrips, useTripWaypoints } from "@/api/trips";
import { useUnassignTasksFromTrip, useTasks } from "@/api/tasks";
import { useShipmentById } from "@/api/shipments";
import { useCarrierScope } from "@/contexts/carrier-scope-context";
import { getOperatorDisplayName } from "@/lib/safe-access";
import { getTaskTypeDisplay } from "@/features/tasks/tasks.helper";
import { Button } from "@/components/ui/button";
import { buildStops, type DerivedStop } from "./trip-stops";
import { taskRefs } from "./task-labels";
import { taskLane, LANE_META } from "./task-type-meta";
import { ShipmentStatusBadge } from "./atoms";
import { shipmentDisplayStatus } from "./status-meta";

interface LookupProps {
  onClose: () => void;
  onOpenTrip: (trip: Trip) => void;
}

type SearchShipment = NonNullable<OrderSearchResult["shipments"]>[number];

/** Where a shipment physically is right now, resolved from the loaded board. */
interface ShipmentPosition {
  trip: Trip;
  stop: DerivedStop;
  index: number;
  taskIds: string[];
}

/** Find the trip/stop a shipment is currently sitting on, by scanning the
 * already-loaded board stops for a task that moves this shipment. Returns null
 * when the shipment isn't on any trip in scope (has a label but isn't routed). */
function findPosition(
  shipmentNumber: number | undefined,
  trips: Trip[],
  stopsByTrip: Map<string, DerivedStop[]>
): ShipmentPosition | null {
  if (shipmentNumber == null) return null;
  for (const trip of trips) {
    const stops = stopsByTrip.get(trip.id) ?? [];
    for (let i = 0; i < stops.length; i++) {
      const matching = stops[i].tasks.filter(
        (t) => taskRefs(t).shipmentNumber === shipmentNumber
      );
      if (matching.length > 0) {
        return {
          trip,
          stop: stops[i],
          index: i,
          taskIds: matching.map((t) => t.id),
        };
      }
    }
  }
  return null;
}

const SEARCH_TYPES: {
  key: OrderSearchType;
  label: string;
  placeholder: string;
}[] = [
  { key: "all", label: "Auto", placeholder: "Tracking #, order #, or phone…" },
  { key: "tracking", label: "Tracking", placeholder: "Tracking number…" },
  { key: "order", label: "Order", placeholder: "Order number…" },
  { key: "shipment", label: "Shipment", placeholder: "Shipment number…" },
  { key: "phone", label: "Phone", placeholder: "Phone number…" },
];

export function Lookup({ onClose, onOpenTrip }: LookupProps) {
  const { carrierIds, dispatchCenterIds } = useCarrierScope();
  const scope = {
    carrierIds: carrierIds.join(","),
    dispatchCenterIds: dispatchCenterIds.join(","),
  };

  // Board data — resolves each shipment's live position without a new endpoint.
  const tripsQuery = useActiveTrips(scope);
  const trips = useMemo(() => tripsQuery.data?.data ?? [], [tripsQuery.data]);
  const waypointQueries = useTripWaypoints(trips);
  const stopsByTrip = useMemo(() => {
    const map = new Map<string, DerivedStop[]>();
    trips.forEach((trip, i) => {
      const wps: Waypoint[] = waypointQueries[i]?.data ?? [];
      map.set(trip.id, buildStops(wps));
    });
    return map;
  }, [trips, waypointQueries]);

  // Order-aware search: accepts tracking #, order #, shipment #, or phone.
  // unfinished=false so a "where's my (just-delivered) order" call still resolves.
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  const [searchType, setSearchType] = useState<OrderSearchType>("all");
  const [selectedNum, setSelectedNum] = useState<number | null>(null);
  const search = useOrderSearch(query, searchType, 20, false);
  const placeholder =
    SEARCH_TYPES.find((t) => t.key === searchType)?.placeholder ??
    "Tracking #, order #, or phone…";

  const order = search.data?.data?.[0];
  const siblings = useMemo<SearchShipment[]>(
    () => order?.shipments ?? [],
    [order]
  );

  // Hero = the shipment the rep actually asked about (matches the query), unless
  // they've picked a sibling. The order is context; the shipment is the subject.
  const matched = useMemo(() => {
    if (siblings.length === 0) return undefined;
    const q = query.trim().toLowerCase();
    const n = parseInt(q.replace(/\D/g, ""), 10);
    return (
      siblings.find((s) => s.trackingNumber?.toLowerCase() === q) ??
      (Number.isNaN(n)
        ? undefined
        : siblings.find((s) => s.shipmentNumber === n))
    );
  }, [siblings, query]);
  const hero =
    siblings.find((s) => s.shipmentNumber === selectedNum) ??
    matched ??
    siblings[0];
  const heroPos = findPosition(hero?.shipmentNumber, trips, stopsByTrip);
  const heroLive = !!heroPos && heroPos.trip.status === TripStatus.ACTIVE;

  const unassign = useUnassignTasksFromTrip({
    onSuccess: () => {
      toast.success("Flagged to dispatch", {
        description: "Stop returned to the Unassigned lane.",
      });
    },
    onError: () => toast.error("Couldn't flag stop"),
  });

  const doSearch = () => {
    setSelectedNum(null);
    setQuery(draft);
  };

  const shareEta = () => {
    if (!hero) return;
    const eta = heroPos?.stop.eta ?? "—";
    const ref = hero.trackingNumber ?? `SHP-${hero.shipmentNumber}`;
    navigator.clipboard?.writeText(`${ref} ETA ${eta}`).catch(() => undefined);
    toast.success("ETA copied", { description: `${ref} · ${eta}` });
  };

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex items-center gap-2 border-b border-zinc-200 px-3.5 py-3">
        <Search className="h-4 w-4 text-indigo-600" />
        <span className="text-[14px] font-bold text-zinc-900">Lookup</span>
        <div className="flex-1" />
        <Button
          variant="ghost"
          size="icon"
          className="size-7 text-zinc-400 hover:bg-zinc-100"
          onClick={onClose}
        >
          <X className="size-[15px]" />
        </Button>
      </div>

      {/* search — order-aware entry with explicit type scoping */}
      <div className="border-b border-zinc-100 px-3.5 py-3">
        <div className="flex h-10 items-center gap-2 rounded-lg bg-zinc-100 px-3 transition-all focus-within:bg-white focus-within:ring-2 focus-within:ring-indigo-500">
          <Search className="h-[15px] w-[15px] text-zinc-400" />
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doSearch()}
            placeholder={placeholder}
            className="flex-1 bg-transparent text-[13px] text-zinc-900 outline-hidden placeholder:text-zinc-400"
          />
          <button
            onClick={doSearch}
            className="h-7 rounded-md bg-indigo-600 px-2.5 text-[11.5px] font-semibold text-white"
          >
            Go
          </button>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {SEARCH_TYPES.map((t) => (
            <button
              key={t.key}
              onClick={() => setSearchType(t.key)}
              className={
                "rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors " +
                (searchType === t.key
                  ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                  : "border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-50")
              }
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {!query ? (
        <EmptyState
          icon={<Search className="mx-auto mb-3 h-10 w-10 opacity-40" />}
          text="Look up a tracking #, order #, or phone to locate a shipment"
        />
      ) : search.isLoading ? (
        <div className="flex flex-1 items-center justify-center text-zinc-400">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Searching…
        </div>
      ) : !order || !hero ? (
        <EmptyState
          icon={<Search className="mx-auto mb-3 h-10 w-10 opacity-40" />}
          text="No shipment matched that tracking #, order #, or phone"
        />
      ) : (
        <div className="flex-1 overflow-y-auto">
          <HeroBlock hero={hero} pos={heroPos} live={heroLive} />
          <ShipmentDetails shipmentId={hero.id} />
          <OrderFanout
            order={order}
            siblings={siblings}
            heroNum={hero.shipmentNumber}
            trips={trips}
            stopsByTrip={stopsByTrip}
            onSelect={(n) => setSelectedNum(n)}
          />
        </div>
      )}

      {order && hero && (
        <div className="flex gap-2 border-t border-zinc-200 px-3.5 py-3">
          {heroPos && (
            <button
              onClick={() => onOpenTrip(heroPos.trip)}
              className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg border border-zinc-200 text-[12px] font-semibold text-zinc-700 hover:bg-zinc-50"
            >
              <Crosshair className="h-3.5 w-3.5" />
              {heroLive ? "Trip focus" : `Open #${heroPos.trip.tripNumber}`}
            </button>
          )}
          <button
            onClick={shareEta}
            className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg border border-zinc-200 text-[12px] font-semibold text-zinc-700 hover:bg-zinc-50"
          >
            <Share2 className="h-3.5 w-3.5" /> Share ETA
          </button>
          {heroPos && (
            <button
              onClick={() =>
                unassign.mutate({
                  tripId: heroPos.trip.id,
                  taskIds: heroPos.taskIds,
                })
              }
              disabled={unassign.isPending}
              className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg bg-orange-500 text-[12px] font-semibold text-white hover:bg-orange-600 disabled:opacity-50"
            >
              <Flag className="h-3.5 w-3.5" /> Flag
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="grid flex-1 place-items-center px-6 text-center">
      <div className="text-zinc-400">
        {icon}
        <div className="text-[13px] font-medium text-zinc-500">{text}</div>
      </div>
    </div>
  );
}

/** The hero: the shipment is the locatable subject; the order is the wrapper.
 * Sender/recipient + contacts live in ShipmentDetails (per-shipment FROM/TO). */
function HeroBlock({
  hero,
  pos,
  live,
}: {
  hero: SearchShipment;
  pos: ShipmentPosition | null;
  live: boolean;
}) {
  const delivered = hero.status === "DELIVERED";

  return (
    <div className="border-b border-zinc-100 px-3.5 py-3.5">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[16px] font-bold text-zinc-900">
          SHP-{hero.shipmentNumber}
        </span>
        <ShipmentStatusBadge status={shipmentDisplayStatus(hero)} />
      </div>
      {hero.trackingNumber && (
        <div className="mt-0.5 font-mono text-[11.5px] text-zinc-400">
          {hero.trackingNumber}
        </div>
      )}

      <div className="mt-3">
        {live && pos ? (
          <LivePosition pos={pos} />
        ) : delivered ? (
          <SummaryNote
            tone="done"
            icon={<Check className="h-4 w-4" strokeWidth={2.5} />}
            title="Delivered"
            detail={
              pos
                ? `Stop ${pos.index + 1} on #${pos.trip.tripNumber}`
                : "Completed"
            }
          />
        ) : pos ? (
          <SummaryNote
            tone="info"
            icon={<Truck className="h-4 w-4" />}
            title={`Routed on #${pos.trip.tripNumber}`}
            detail={
              pos.trip.operator
                ? `${getOperatorDisplayName(pos.trip.operator)} · not started`
                : "Not started yet"
            }
          />
        ) : (
          <SummaryNote
            tone="warn"
            icon={<Package className="h-4 w-4" />}
            title="Not on a trip yet"
            detail="Has a label but isn't routed onto a trip"
          />
        )}
      </div>
    </div>
  );
}

const SUMMARY_TONE = {
  done: { bg: "#f0fdf4", border: "#bbf7d0", fg: "#15803d" },
  info: { bg: "#eef2ff", border: "#c7d2fe", fg: "#4338ca" },
  warn: { bg: "#fff7ed", border: "#fed7aa", fg: "#c2410c" },
};

function SummaryNote({
  tone,
  icon,
  title,
  detail,
}: {
  tone: keyof typeof SUMMARY_TONE;
  icon: React.ReactNode;
  title: string;
  detail: string;
}) {
  const t = SUMMARY_TONE[tone];
  return (
    <div
      className="flex items-center gap-2.5 rounded-lg px-3 py-2.5"
      style={{ background: t.bg, border: `1px solid ${t.border}` }}
    >
      <span style={{ color: t.fg }}>{icon}</span>
      <div className="min-w-0">
        <div className="text-[12.5px] font-bold" style={{ color: t.fg }}>
          {title}
        </div>
        <div className="text-[11.5px] text-zinc-500">{detail}</div>
      </div>
    </div>
  );
}

/** Full live position — only shown when the shipment is on an ACTIVE trip. */
function LivePosition({ pos }: { pos: ShipmentPosition }) {
  const { trip, stop, index } = pos;
  const color = trip.color ?? "#6366f1";
  const driver = trip.operator;

  return (
    <div className="overflow-hidden rounded-lg border border-indigo-200">
      <div className="flex items-center gap-2 bg-indigo-50 px-3 py-2 text-[11.5px]">
        <Truck className="h-3.5 w-3.5 text-indigo-600" />
        <span className="font-mono font-bold text-indigo-700">
          #{trip.tripNumber}
        </span>
        {driver && (
          <span className="font-semibold text-zinc-700">
            {getOperatorDisplayName(driver)}
          </span>
        )}
        <span className="ml-auto inline-flex items-center gap-1 font-semibold text-green-600">
          <span
            className="live-dot rounded-full"
            style={{ width: 7, height: 7, background: "#16a34a" }}
          />
          live
        </span>
      </div>
      <div className="grid grid-cols-2 divide-x divide-zinc-100">
        <div className="px-3 py-2.5 text-center">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
            This stop
          </div>
          <div className="font-mono text-[15px] font-bold tabular-nums text-zinc-900">
            {index + 1}
          </div>
        </div>
        <div className="px-3 py-2.5 text-center">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
            ETA
          </div>
          <div className="font-mono text-[15px] font-bold tabular-nums text-indigo-600">
            {stop.eta ?? "—"}
          </div>
        </div>
      </div>
      <div className="flex items-start gap-2 border-t border-zinc-100 px-3 py-2.5">
        <span
          className="mt-0.5 grid h-[20px] w-[20px] place-items-center rounded-md font-mono text-[10px] font-bold tabular-nums text-white"
          style={{ background: color }}
        >
          {stop.seq}
        </span>
        <div className="min-w-0 flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
          <span className="truncate text-[12px] text-zinc-700">
            {stop.addr}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Full hero detail — packages + per-task cards. Fetched only when a hero is
 * selected (this child mounts conditionally), so the locator stays one call
 * until the rep wants the depth. */
function ShipmentDetails({ shipmentId }: { shipmentId: string }) {
  const { data: shipment, isLoading } = useShipmentById(shipmentId);
  const { data: tasksResp } = useTasks({ shipmentId, limit: "all" });
  const packages = shipment?.packages ?? [];
  const weight = packages.reduce((n, p) => n + (p.weightGrams ?? 0), 0);
  const fragile = packages.some((p) => p.isFragile);
  const cold = packages.some((p) => p.requiresCooling);
  const tasks = tasksResp?.data ?? [];
  const addresses = shipment?.addresses ?? [];
  const sender = addresses.find((a) => a.type === "FROM");
  const recipient = addresses.find((a) => a.type === "TO");

  const empty =
    packages.length === 0 && tasks.length === 0 && !sender && !recipient;

  if (isLoading && empty) {
    return (
      <div className="flex items-center gap-2 border-t border-zinc-100 px-3.5 py-3 text-[12px] text-zinc-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading details…
      </div>
    );
  }
  if (empty) return null;

  return (
    <div className="space-y-2.5 border-t border-zinc-100 px-3.5 py-3">
      {(!!sender || !!recipient) && (
        <div className="flex items-stretch gap-2">
          <Party role="Sender" addr={sender} />
          <div className="grid place-items-center px-0.5 text-zinc-300">
            <ArrowRight className="h-3.5 w-3.5" />
          </div>
          <Party role="Recipient" addr={recipient} />
        </div>
      )}
      {packages.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-[11.5px] text-zinc-600">
          <Package className="h-3.5 w-3.5 text-zinc-400" />
          <span className="tabular-nums">
            {packages.length} pkg · {weight} g
          </span>
          {fragile && (
            <CareChip
              icon={<AlertTriangle className="h-3 w-3" />}
              label="Fragile"
            />
          )}
          {cold && (
            <CareChip icon={<Snowflake className="h-3 w-3" />} label="Cold" />
          )}
        </div>
      )}
      {tasks.length > 0 && (
        <div className="space-y-1">
          {tasks.map((t) => (
            <TaskLine key={t.id} task={t} />
          ))}
        </div>
      )}
    </div>
  );
}

function CareChip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] font-semibold text-white"
      style={{ background: "#0d9488" }}
    >
      {icon} {label}
    </span>
  );
}

/** One party (sender or recipient) with contact + phone, from the shipment's
 * FROM/TO address. Phone is a tel: link so a CS rep can dial straight from it. */
function Party({ role, addr }: { role: string; addr?: ShipmentAddress }) {
  const line = addr
    ? [
        addr.addressLine1 ?? addr.address?.line1,
        addr.addressCity ?? addr.address?.city,
      ]
        .filter(Boolean)
        .join(", ")
    : "";
  return (
    <div className="min-w-0 flex-1">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
        {role}
      </div>
      <div className="truncate text-[12.5px] font-semibold text-zinc-800">
        {addr?.contactName ?? "—"}
      </div>
      {line && <div className="truncate text-[11px] text-zinc-500">{line}</div>}
      {addr?.contactPhone && (
        <a
          href={`tel:${addr.contactPhone}`}
          className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:underline"
        >
          <Phone className="h-3 w-3" /> {addr.contactPhone}
        </a>
      )}
    </div>
  );
}

const TASK_STATUS_COLOR: Record<string, string> = {
  [TaskStatus.COMPLETED]: "#15803d",
  [TaskStatus.FAILED]: "#b91c1c",
  [TaskStatus.IN_PROGRESS]: "#c2410c",
  [TaskStatus.CANCELLED]: "#b91c1c",
  [TaskStatus.PENDING]: "#71717a",
};

function TaskLine({ task }: { task: Task }) {
  const lane = taskLane(task.type);
  return (
    <div className="flex items-center gap-2">
      <span
        className="h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ background: LANE_META[lane].color }}
      />
      <span className="text-[11.5px] font-semibold text-zinc-700">
        {getTaskTypeDisplay(task.type)}
      </span>
      <span className="font-mono text-[10.5px] text-zinc-400">
        TSK-{task.taskNumber}
      </span>
      <span
        className="ml-auto text-[10px] font-bold uppercase tracking-wide"
        style={{ color: TASK_STATUS_COLOR[task.status] ?? "#71717a" }}
      >
        {task.status.replace(/_/g, " ")}
      </span>
    </div>
  );
}

/** The order fan-out: every sibling shipment, selectable, with its own
 * status + trip. A split order has no single position — this shows all parts. */
function OrderFanout({
  order,
  siblings,
  heroNum,
  trips,
  stopsByTrip,
  onSelect,
}: {
  order: OrderSearchResult;
  siblings: SearchShipment[];
  heroNum: number | undefined;
  trips: Trip[];
  stopsByTrip: Map<string, DerivedStop[]>;
  onSelect: (n: number) => void;
}) {
  return (
    <div className="px-3 py-3">
      <div className="mb-1.5 flex items-center gap-1.5 px-1">
        <span className="font-mono text-[12px] font-bold text-zinc-700">
          ORD-{order.orderNumber}
        </span>
        <span className="text-[11.5px] text-zinc-400">
          · {siblings.length} shipment{siblings.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="space-y-1.5">
        {siblings.map((s) => {
          const pos = findPosition(s.shipmentNumber, trips, stopsByTrip);
          const isHero = s.shipmentNumber === heroNum;
          const driver = pos?.trip.operator;
          const summary =
            s.status === "DELIVERED"
              ? "Delivered"
              : pos
                ? `on #${pos.trip.tripNumber}${
                    driver ? ` · ${getOperatorDisplayName(driver)}` : ""
                  }`
                : "Not routed";
          return (
            <button
              key={s.id}
              onClick={() =>
                s.shipmentNumber != null && onSelect(s.shipmentNumber)
              }
              className={
                "flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors " +
                (isHero
                  ? "border-indigo-300 bg-indigo-50/60 ring-1 ring-indigo-200"
                  : "border-zinc-200 bg-white hover:bg-zinc-50")
              }
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-[12px] font-semibold text-zinc-900">
                    SHP-{s.shipmentNumber}
                  </span>
                  <ShipmentStatusBadge status={shipmentDisplayStatus(s)} />
                </div>
                <div className="mt-0.5 truncate text-[11px] text-zinc-500">
                  {summary}
                </div>
              </div>
              {isHero && (
                <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-indigo-600">
                  Viewing
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
